import { useEffect, useState, useCallback, useRef } from 'react';
import type { Session, AuthChangeEvent } from '@supabase/supabase-js';
import type { AuthCoreConfig, AuthCoreState } from './types';

const SESSION_REFRESH_INTERVAL = 5 * 60 * 1000; // 5 minutes

/**
 * Core authentication hook that manages session state and provides
 * platform-agnostic auth operations. Each app wraps this with its
 * own AuthContext, adding platform-specific methods (OAuth, navigation, etc.).
 */
export function useAuthCore(config: AuthCoreConfig): AuthCoreState {
  const { supabaseClient, clearCaches } = config;

  const [session, setSession] = useState<Session | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const sessionRef = useRef<Session | null>(null);

  // Use refs for callbacks to avoid effect re-runs when callers pass inline functions
  const onSessionExpiredRef = useRef(config.onSessionExpired);
  const onPasswordRecoveryRef = useRef(config.onPasswordRecovery);
  useEffect(() => {
    onSessionExpiredRef.current = config.onSessionExpired;
    onPasswordRecoveryRef.current = config.onPasswordRecovery;
  });

  // Keep sessionRef in sync with session state
  useEffect(() => {
    sessionRef.current = session;
  }, [session]);

  useEffect(() => {
    // Get initial session
    supabaseClient.auth.getSession().then(({ data: { session: initialSession } }) => {
      setSession(initialSession);
      setIsLoading(false);
    });

    // Listen for auth changes
    const {
      data: { subscription },
    } = supabaseClient.auth.onAuthStateChange((event: AuthChangeEvent, newSession) => {
      setSession(newSession);

      // Redirect to reset password page on PASSWORD_RECOVERY event
      if (event === 'PASSWORD_RECOVERY') {
        onPasswordRecoveryRef.current?.();
      }

      // Handle token refresh errors - when session expires and can't be refreshed
      if (event === 'TOKEN_REFRESHED' && !newSession) {
        onSessionExpiredRef.current();
      }
    });

    // Set up periodic token refresh check
    const refreshInterval = setInterval(async () => {
      const { data, error } = await supabaseClient.auth.getSession();
      if (error || !data.session) {
        // Session is invalid or expired
        if (sessionRef.current) {
          // We had a session but now don't - token refresh failed
          setSession(null);
          onSessionExpiredRef.current();
        }
      }
    }, SESSION_REFRESH_INTERVAL);

    return () => {
      subscription.unsubscribe();
      clearInterval(refreshInterval);
    };
  }, [supabaseClient]);

  const signIn = useCallback(
    async (email: string, password: string) => {
      const { error } = await supabaseClient.auth.signInWithPassword({
        email,
        password,
      });
      if (error) throw error;
    },
    [supabaseClient]
  );

  const signUp = useCallback(
    async (email: string, password: string, name?: string) => {
      const { error } = await supabaseClient.auth.signUp({
        email,
        password,
        options: {
          data: { name },
        },
      });
      if (error) throw error;
    },
    [supabaseClient]
  );

  const signOut = useCallback(async () => {
    const { error } = await supabaseClient.auth.signOut();
    if (error) throw error;
    await clearCaches();
  }, [supabaseClient, clearCaches]);

  const getToken = useCallback(async () => {
    const {
      data: { session: currentSession },
    } = await supabaseClient.auth.getSession();
    return currentSession?.access_token ?? null;
  }, [supabaseClient]);

  const updatePassword = useCallback(
    async (newPassword: string) => {
      const { error } = await supabaseClient.auth.updateUser({
        password: newPassword,
      });
      if (error) throw error;
    },
    [supabaseClient]
  );

  const updateEmail = useCallback(
    async (newEmail: string) => {
      const { error } = await supabaseClient.auth.updateUser({
        email: newEmail,
      });
      if (error) throw error;
    },
    [supabaseClient]
  );

  const resendConfirmation = useCallback(
    async (email: string) => {
      const { error } = await supabaseClient.auth.resend({
        type: 'signup',
        email,
      });
      if (error) throw error;
    },
    [supabaseClient]
  );

  const getLinkedIdentities = useCallback(async () => {
    const { data, error } = await supabaseClient.auth.getUserIdentities();
    if (error) throw error;
    return data?.identities ?? [];
  }, [supabaseClient]);

  const unlinkProvider = useCallback(
    async (identity: Parameters<typeof supabaseClient.auth.unlinkIdentity>[0]) => {
      const { error } = await supabaseClient.auth.unlinkIdentity(identity);
      if (error) throw error;
    },
    [supabaseClient]
  );

  return {
    session,
    user: session?.user ?? null,
    isLoading,
    signIn,
    signUp,
    signOut,
    getToken,
    updatePassword,
    updateEmail,
    resendConfirmation,
    getLinkedIdentities,
    unlinkProvider,
  };
}
